import { StatsContexterServiceInterface } from '../../service/stats-contexter/service.interface';
import { StatsContexterOptionsInterface } from './contexter-options.interface';
import { StatsContextGlobalInterface } from './context/global.interface';
import { StatsContextPropertySearchInterface } from './context/property-search.interface';
import { StatsContextAgentSearchInterface } from './context/agent-search.interface';
import { StatsContextBrokerSearchInterface } from './context/broker-search.interface';
import { StatsContextLocalInterface } from './context/local.interface';
import { StatsContextInterface } from './context.interface';
import { WindowServiceInterface } from '../../service/window/service.interface';
import { StatsDataUserInterface } from './data/user.interface';
import { AuthenticationProviderType } from './types';
import { StatsContextAbTestsInterface } from './context/ab-tests.interface';

export class StatsContexter implements StatsContexterServiceInterface {
  /**
   * Current global context
   */
  protected globalContext: StatsContextGlobalInterface;

  /**
   *
   * @param windowService
   */
  constructor(private windowService: WindowServiceInterface) {
    this.reset();
  }

  /**
   * @inheritDoc
   */
  public initialize(options: StatsContexterOptionsInterface): void {
    this.setApplication(options.application);
    this.setCountry(options.country);
    this.setCurrency(options.currency);
    this.setLanguage(options.language);
    this.setAuthenticationToken(options.authentication.token);
  }

  /**
   * Reset the context
   */
  public reset(): void {
    this.globalContext = {
      url: '',
      application: '',
      authentication: {
        token: '',
        user: null,
        provider: null,
      },
      language: '',
    };
  }

  /**
   * @inheritDoc
   */
  public getContext(localContext?: StatsContextLocalInterface): StatsContextInterface {
    return {
      global: this.getGlobalContext(),
      local: localContext || {},
    };
  }

  /**
   * @inheritDoc
   */
  public getGlobalContext(): StatsContextGlobalInterface {
    return {
      ...this.globalContext,
      url: this.windowService.getNative().location.href, // Set current URL on runtime
    };
  }

  /**
   * @inheritDoc
   */
  public setApplication(application: string): void {
    this.globalContext.application = application;
  }

  /**
   * @inheritDoc
   */
  public setAuthenticationToken(token: string): void {
    this.globalContext.authentication.token = token;
  }

  /**
   * @inheritDoc
   */
  public setAuthenticationUser(user: StatsDataUserInterface | null): void {
    this.globalContext.authentication.user = user;
  }

  /**
   * @inheritDoc
   */
  public setAuthenticationProvider(provider: AuthenticationProviderType | null): void {
    this.globalContext.authentication.provider = provider;
  }

  /**
   * @inheritDoc
   */
  public setAuthenticationAttributionId(id: string): void {
    this.globalContext.authentication.attributionId = id;
  }

  /**
   * @inheritdoc
   */
  public setCountry(country: string): void {
    this.globalContext.country = country || '';
  }

  /**
   * @inheritDoc
   */
  public setCurrency(currency: string): void {
    this.globalContext.currency = currency;
  }

  /**
   * @inheritDoc
   */
  public setLanguage(language: string): void {
    this.globalContext.language = language;
  }

  /**
   * @inheritDoc
   */
  public setPropertyCategoryIdentifier(identifier: string): void {
    this.globalContext.property_category_identifier = identifier;
  }

  /**
   * @inheritDoc
   */
  public setErrorPage(errorPage: boolean): void {
    this.globalContext.error_page = errorPage;
  }

  /**
   * @inheritDoc
   */
  public setHomePage(homePage: boolean): void {
    this.globalContext.home_page = homePage;
  }

  /**
   * @inheritDoc
   */
  public setPropertyPage(propertyPage: boolean): void {
    this.globalContext.property_page = propertyPage;
  }

  /**
   * @inheritDoc
   */
  public setPropertyPageWithCommunityExpert(propertyPage: boolean): void {
    this.globalContext.property_page_with_community_expert = propertyPage;
  }

  /**
   * @inheritDoc
   */
  public setPropertyPageOffline(propertyPageOffline: boolean): void {
    this.globalContext.property_page_offline = propertyPageOffline;
  }

  /**
   * @inheritDoc
   */
  public setPropertySerp(propertySerp: boolean): void {
    this.globalContext.property_serp = propertySerp;
  }

  /**
   * @inheritDoc
   */
  public setMapSearchPage(mapSearch: boolean): void {
    this.globalContext.map_search_page = mapSearch;
  }

  /**
   * @inheritDoc
   */
  public setPropertySearch(propertySearch: StatsContextPropertySearchInterface): void {
    this.globalContext.property_search = propertySearch;
  }

  /**
   * @inheritDoc
   */
  public setPropertyId(propertyId: number): void {
    this.globalContext.property_id = propertyId;
  }

  /**
   * @inheritDoc
   */
  public setAgentHome(agentHome: boolean): void {
    this.globalContext.agent_home = agentHome;
  }

  /**
   * @inheritDoc
   */
  public setAgentPage(agentPage: boolean): void {
    this.globalContext.agent_page = agentPage;
  }

  /**
   * @inheritDoc
   */
  public setAgentSerp(agentSerp: boolean): void {
    this.globalContext.agent_serp = agentSerp;
  }

  /**
   * @inheritDoc
   */
  public setAgentSearch(agentSearch: StatsContextAgentSearchInterface): void {
    this.globalContext.agent_search = agentSearch;
  }

  /**
   * @inheritDoc
   */
  public setAgentId(agentId: number): void {
    this.globalContext.agent_id = agentId;
  }

  /**
   * @inheritDoc
   */
  public setBrokerHome(brokerHome: boolean): void {
    this.globalContext.broker_home = brokerHome;
  }

  /**
   * @inheritDoc
   */
  public setBrokerPage(brokerPage: boolean): void {
    this.globalContext.broker_page = brokerPage;
  }

  /**
   * @inheritDoc
   */
  public setBrokerSerp(brokerSerp: boolean): void {
    this.globalContext.broker_serp = brokerSerp;
  }

  /**
   * @inheritDoc
   */
  public setBrokerSearch(brokerSearch: StatsContextBrokerSearchInterface): void {
    this.globalContext.broker_search = brokerSearch;
  }

  /**
   * @inheritDoc
   */
  public setBrokerId(brokerId: number): void {
    this.globalContext.broker_id = brokerId;
  }

  /**
   * @inheritDoc
   */
  public setUserPage(userPage: boolean): void {
    this.globalContext.user_page = userPage;
  }

  /**
   * @inheritDoc
   */
  public setMyAccountContactedPage(myAccountContacted: boolean): void {
    this.globalContext.contacted_properties = myAccountContacted;
  }

  /**
   * @inheritDoc
   */
  public setMyAccountSavedPage(myAccountSaved: boolean): void {
    this.globalContext.saved_properties = myAccountSaved;
  }

  /**
   * @inheritDoc
   */
  public setMyAccountMyNotesPage(myAccountMyNotes: boolean): void {
    this.globalContext.my_notes_properties = myAccountMyNotes;
  }

  /**
   * @inheritDoc
   */
  public setBuyVsRentCalculatorPage(calculatorPage: boolean): void {
    this.globalContext.buyvsrent_calculator = calculatorPage;
  }

  /**
   * @inheritDoc
   */
  public setHomeValuationPage(homeValuationPage: boolean): void {
    this.globalContext.home_valuation = homeValuationPage;
  }

  /**
   * @inheritdoc
   */
  public setAbTests(abTests: StatsContextAbTestsInterface): void {
    this.globalContext.abTests = abTests;
  }

  /**
   * @inheritdoc
   */
  public setProjectDetail(projectDetail: boolean): void {
    this.globalContext.project_detail = projectDetail;
  }
}
